import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { Form } from 'antd';
import { DarwinRow, DarwinDivider, DarwinSpin } from 'components/darwin-ui';
import {
  AvatarProductUploadInput,
  ProductMetaDataSection,
} from 'components/ui/products/newProductForm';
import styles from '../Products/Products.module.css';
import {
  useCreateProduct,
  useEditProduct,
  useGetProductById,
  useGetProductsTree,
  useUploadProductAvatar,
} from 'api/services/productsService';
import { ProductInformationSection } from 'components/ui/products/newProductForm/sections/NewProductInformationSection';
import { EUDRSection } from 'components/ui/products/newProductForm/sections/EUDRSection';
import { transformToTreeProduct } from 'components/ui/products/newProductForm/utils/TreeSelectProduct';
import { useUI } from 'contexts/UiContext';
import { CreateProductPayload } from 'types/productsTypes';
import { extractQueryData } from 'utils/services';
import { parseISO } from 'date-fns';
import { ROUTES } from 'routes/routes';

type CreateProductFormProps = {
  formAction?: 'create' | 'edit' | 'view';
};

export type FormProduct = {
  avatar: File[] | null | string;
  productName: string;
  traceabilityProductCode?: string;
  productID: string;
  externalId?: string;
  sku: string;
  gtin: string;
  description: string;
  category: string;
  finalProduct: boolean;
  createdBy?: string | Date;
  modifiedBy?: string | Date;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  isEudrEnabled: boolean;
  hsCode: string | null;
};

const baseDefaultProductValues = {
  avatar: null,
  productName: '',
  traceabilityProductCode: '',
  productID: '',
  externalId: '',
  sku: '',
  gtin: '',
  description: '',
  category: '',
  finalProduct: false,
  isEudrEnabled: false,
  hsCode: null,
};

export const CreateProductForm = ({
  formAction = 'create',
}: CreateProductFormProps) => {
  const isCreateForm = formAction === 'create';
  const isEditForm = formAction === 'edit';
  const isViewForm = formAction === 'view';
  const defaultValuesAdditionalProperties = !isCreateForm
    ? {
        createdBy: '',
        modifiedBy: '',
        createdDate: '',
        modifiedDate: '',
      }
    : {};
  const { selectedCompany } = useUI();
  const [initialValuesOnEditForm, setInitialValuesOnEditForm] =
    useState<FormProduct>({
      avatar: null,
      productName: '',
      traceabilityProductCode: '',
      productID: '',
      externalId: '',
      sku: '',
      gtin: '',
      description: '',
      category: '',
      finalProduct: false,
      isEudrEnabled: false,
      hsCode: null,
    });

  // hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
    setValue,
  } = useForm<FormProduct>({
    defaultValues: {
      ...baseDefaultProductValues,
      ...defaultValuesAdditionalProperties,
    },
    mode: 'onChange',
  });

  // get inputs value
  const avatarImageUrl = watch('avatar');

  // detect if any field has been edited on edit form
  const currentValues = useWatch({ control });

  const isEditFormModified =
    JSON.stringify(currentValues) !== JSON.stringify(initialValuesOnEditForm);

  // disable button and spinner logic
  const isSaveButtonDisabled = isViewForm || !isEditFormModified || !isValid;

  // get all products tree category and formatted for category field
  const { data: productsTreeData } = useGetProductsTree(selectedCompany || '');
  const productsTreeDataFormatted = transformToTreeProduct(
    productsTreeData?.data,
    t
  );

  // get initial data when formAction = view | edit
  const { id: productId } = useParams();
  const productByIdQuery = useGetProductById(
    selectedCompany || '',
    productId || ''
  );
  const productByIdData = extractQueryData(productByIdQuery, undefined);

  const buildInitialValues = (data: CreateProductPayload) => {
    return {
      avatar: data.imageFileId || null,
      productName: data.name,
      traceabilityProductCode: undefined,
      productID: productId || '',
      externalId: data.externalId,
      sku: data.sku,
      gtin: data.gtin,
      description: data.description,
      category: data.productId,
      finalProduct: data.finalProduct,
      isEudrEnabled: data.isEudrEnabled,
      hsCode: data.hsCode,
    };
  };

  useEffect(() => {
    if (!isCreateForm && productByIdData && selectedCompany) {
      const {
        name: productName,
        id: productID,
        traceabilityProductCode,
        sku,
        gtin,
        description,
        finalProduct,
        signedUrl: avatar,
        createdBy,
        createdAt,
        updatedAt,
        updatedBy,
        externalId,
        isEudrEnabled,
        hsCode,
      } = productByIdData;

      const viewOrEditPayload = {
        avatar,
        productName,
        traceabilityProductCode,
        productID,
        externalId,
        sku,
        gtin,
        description,
        category: productByIdData.product.id,
        finalProduct,
        isEudrEnabled,
        hsCode,
        ...(isViewForm && {
          createdBy: createdBy
            ? `${createdBy?.firstName} ${createdBy?.lastName}`
            : '',
          createdAt: parseISO(createdAt),
          modifiedBy: updatedBy
            ? `${updatedBy?.firstName} ${updatedBy?.lastName}`
            : '',
          updatedAt: parseISO(updatedAt),
        }),
      };
      reset(viewOrEditPayload);
      setInitialValuesOnEditForm(viewOrEditPayload);
    }
  }, [isCreateForm, isViewForm, productByIdData, reset, selectedCompany]);

  // avatar logic
  const {
    mutateAsync: uploadProductAvatar,
    isPending: isUploadingProductAvatarPending,
  } = useUploadProductAvatar();

  const handleUploadProductAvatar = async (file: File) => {
    try {
      const uploadProductAvatarResponse = await uploadProductAvatar({
        file,
      });
      return uploadProductAvatarResponse.data.id;
    } catch (error) {
      console.log(error);
    }
  };

  // create product logic
  const { mutateAsync: createProduct, isPending: isCreateProductPending } =
    useCreateProduct();

  const handleCreateProduct = (
    payload: CreateProductPayload,
    saveAndExit: boolean
  ) => {
    createProduct({
      productData: payload,
      companyId: selectedCompany || '',
    })
      .then(response => {
        openNotification(
          t('success'),
          t('product.new-product-form.create-new-product-success'),
          'success'
        );
        if (saveAndExit) navigate(ROUTES.PRODUCTS.LIST);
        if (!saveAndExit) {
          navigate(
            generatePath(ROUTES.PRODUCTS.EDIT, {
              id: response.data.id.toString(),
            })
          );
        }
      })
      .catch(() =>
        openNotification(
          t('error'),
          t('product.new-product-form.create-new-product-error'),
          'error'
        )
      );
  };

  // edit product logic
  const { mutateAsync: editProduct, isPending: isEditProductPending } =
    useEditProduct();

  const handleEditProduct = (
    payload: CreateProductPayload,
    saveAndExit: boolean
  ) => {
    editProduct({
      productData: payload,
      companyId: selectedCompany || '',
      productId: productId || '',
    })
      .then(() => {
        openNotification(
          t('success'),
          t('product.edit.edit-product-success'),
          'success'
        );
        if (saveAndExit) navigate(ROUTES.PRODUCTS.LIST);
        const data = buildInitialValues(payload);
        if (!saveAndExit) setInitialValuesOnEditForm(data);
      })
      .catch(() =>
        openNotification(
          t('error'),
          t('product.edit.edit-product-error'),
          'error'
        )
      );
  };

  // submit form logic
  const onSubmit =
    (saveAndExit: boolean): SubmitHandler<FormProduct> =>
    async data => {
      // upload avatar
      let imageUrl;
      if (
        data.avatar &&
        data.avatar?.length > 0 &&
        typeof data.avatar !== 'string'
      ) {
        imageUrl = await handleUploadProductAvatar(data.avatar[0]);
      }

      const {
        productName,
        description,
        sku,
        gtin,
        finalProduct,
        externalId,
        isEudrEnabled,
        hsCode,
      } = data;

      const payload = {
        name: productName,
        description,
        sku,
        gtin,
        finalProduct,
        productId: data.category,
        imageFileId: imageUrl,
        externalId,
        isEudrEnabled,
        hsCode,
      };

      isCreateForm
        ? handleCreateProduct(payload, saveAndExit)
        : handleEditProduct(payload, saveAndExit);
    };

  const isLoadingInitialDataOrSubmittingForm =
    productByIdQuery.isLoading ||
    isUploadingProductAvatarPending ||
    isEditProductPending ||
    isCreateProductPending;

  return (
    <>
      <ContentHeader
        title={
          isCreateForm
            ? t('product.new-product-form.title')
            : isEditForm
              ? t('product.edit.title')
              : t('product.product-view')
        }
        onSave={handleSubmit(onSubmit(false))}
        onCancel={() => navigate(ROUTES.PRODUCTS.LIST)}
        saveDisabled={isSaveButtonDisabled}
        extraButtonDisabled={isSaveButtonDisabled}
        displayExtraButton={true}
        extraButtonTitle={t('save-exit')}
        onExtraButtonClick={handleSubmit(onSubmit(true))}
      />
      <DarwinSpin fullscreen spinning={isLoadingInitialDataOrSubmittingForm} />
      <Form
        className={styles.productFormContainer}
        layout="vertical"
        onFinish={handleSubmit(onSubmit(false))}
      >
        {/* Avatar - This section is being commented on by Demo realizations */}

        {/* <DarwinRow align={'middle'}>
          <AvatarProductUploadInput
            control={control}
            errors={errors}
            isCreateOrEditForm={isCreateForm || isEditForm}
            imageUrl={avatarImageUrl}
          />
        </DarwinRow>

        <DarwinDivider /> */}

        {/* Product Section */}
        <ProductInformationSection
          isViewForm={isViewForm}
          control={control}
          errors={errors}
          productDataCategory={productsTreeDataFormatted}
        />

        <EUDRSection
          control={control}
          errors={errors}
          isViewForm={isViewForm}
          setValue={setValue}
        />

        {isViewForm && (
          <ProductMetaDataSection control={control} errors={errors} />
        )}
      </Form>
    </>
  );
};
